/**
 * Phase 530 wave 4 task 4.3 (#1939) — the chase list: everything the firm is still waiting on,
 * ACROSS files, oldest owed first.
 *
 * <h3>Why this is a read of its own and not a sum of the board</h3>
 * <p>It is asked at a scope the board cannot reach. The board is scoped to ONE declared spine by
 * construction, while a firm chases what IT is owed across every workflow it runs — so adding up
 * the board's `outstanding_count` column would answer a narrower question and quietly call it the
 * wider one. The engine serves it as its own read (`GET /internal/v0/job-status/outstanding`), and
 * each row arrives NAMED: the file's client name and stage ride on the row, so a forty-item list is
 * one call rather than forty-one.</p>
 *
 * <h3>Grouped by file, and still oldest-first</h3>
 * <p>The engine orders the whole list by `since` ascending — the chase order. A staffer, though,
 * chases a FILE and not an item: two things owed by the same client are one phone call. So the page
 * groups by file while preserving that order exactly: groups appear in the order their OLDEST item
 * did (first appearance wins), and within a group the engine's order is untouched. Nothing is
 * re-sorted here, which matters on a CAPPED list — a client-side sort would promise "the oldest
 * thing you are owed" while only ever seeing the first hundred rows the engine sent.</p>
 *
 * <h3>What the page refuses to do</h3>
 * <ul>
 *   <li><b>No job id in anything a staffer reads</b> (AC-5) — the group heading is a LINK whose
 *       `href` carries the id, and that is the one place it appears.</li>
 *   <li><b>No invented name</b> — a file whose declared display-name path resolves to nothing reads
 *       "no client name recorded", never its id and never a blank heading.</li>
 *   <li><b>No silent truncation</b> — `cap_hit` is disclosed in words using the envelope's
 *       EFFECTIVE `limit`, exactly as the board discloses its own.</li>
 * </ul>
 *
 * <p>The row itself is the file detail's row — {@link OutstandingItemLine} — because it is the same
 * record: the engine NESTS the very `JobStatusOutstandingItem` the detail carries rather than
 * flattening a second copy of it onto this row, so this page renders it with the same component and
 * the same stylesheet.</p>
 *
 * <p>Design tokens only — no raw hex (test/polish/hex-fence.test.ts).</p>
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Button, EmptyState, LastUpdated, PageHeader } from "@ardept/design-system";
import { useClientTenant, useJobStatusOutstanding } from "../api/portalQueries.js";
import type { JobStatusOutstandingRow } from "../api/types.js";
import { ONBOARDING_LABEL } from "../nav/familyRegistry.js";
import { usePageTitle } from "../nav/usePageTitle.js";
import {
  OutstandingItemLine,
  fileDisplayName,
  outstandingItemKey,
} from "./FileDetailPage.js";
import "./FilesBoardPage.css";
import "./FileDetailPage.css";
import { useVendorCopy } from "../theme/vendorCopy.js";

/** The page's own name — the nav has no entry for it, so this is the only place it is spelled. */
export const OUTSTANDING_LABEL = "Outstanding items";

/** Where the back link goes, and what it says. */
const BACK_TO_BOARD = `All ${ONBOARDING_LABEL.toLowerCase()} files`;

/** One file's worth of the chase list. `jobId` is a ROUTING KEY — the link target, never copy. */
export interface FileGroup {
  jobId: string;
  clientName: string | null;
  stageLabel: string;
  items: JobStatusOutstandingRow[];
}

/**
 * The engine's flat, oldest-first list, grouped by file WITHOUT re-ordering anything.
 *
 * <p>A `Map` keyed by file id: insertion order is iteration order in JavaScript, so a group takes
 * the position of its own oldest item and every item keeps the place the engine gave it. That is
 * the whole algorithm, and it is written as a function so the ordering claim is testable without
 * rendering a page.</p>
 *
 * <p>Two rows of one file CAN disagree about its name only if the engine sent them that way, which
 * it cannot: the client name and stage are resolved ONCE per file, from one declaration, in the same
 * pass. So the first row's copy is the group's copy.</p>
 */
export function groupByFile(rows: readonly JobStatusOutstandingRow[]): FileGroup[] {
  const groups = new Map<string, FileGroup>();
  for (const row of rows) {
    const existing = groups.get(row.job_id);
    if (existing) {
      existing.items.push(row);
      continue;
    }
    groups.set(row.job_id, {
      jobId: row.job_id,
      clientName: row.client_name,
      stageLabel: row.stage_label,
      items: [row],
    });
  }
  return [...groups.values()];
}

export function OutstandingItemsPage(): JSX.Element {
  const { contact } = useVendorCopy();
  const identity = useClientTenant();
  const tenantId = identity.tenantId;

  usePageTitle(OUTSTANDING_LABEL);

  const chase = useJobStatusOutstanding(tenantId);
  const data = chase.data;

  // Read once per paint, so every age in the list measures against the SAME instant.
  const now = useMemo(() => Date.now(), [data]);
  const groups = useMemo(() => groupByFile(data?.items ?? []), [data]);

  // "Still loading" is defined by the ANSWER, not by a fetch flag: the query is disabled until
  // `/me` yields a tenant, and a disabled query reports isLoading:false — which would flash the
  // "nothing outstanding" state at a staffer who is owed plenty.
  const unresolved = tenantId !== null && data === undefined && chase.error === null;
  const isBusy = identity.isLoading || unresolved;
  const failed = identity.error ?? chase.error ?? null;

  // An empty list is a 200 and a real answer — "nothing is outstanding" — never an error.
  const isEmpty = !!data && data.items.length === 0;

  return (
    <div className="outstanding-items" data-testid="onboarding-outstanding">
      <Link className="fd-back" to="/onboarding">
        <span aria-hidden="true">&larr;</span> {BACK_TO_BOARD}
      </Link>

      <PageHeader
        title={OUTSTANDING_LABEL}
        subtitle="Everything you are still waiting on, across all your onboarding files, with the longest-outstanding first."
        actions={
          <Button
            variant="ghost"
            onClick={() => void chase.refetch()}
            disabled={chase.isFetching || !tenantId}
          >
            {chase.isFetching ? "Refreshing…" : "Refresh"}
          </Button>
        }
      />

      {failed && (
        <div className="state state--error" role="status">
          <h2 className="state__title">We could not load what is outstanding</h2>
          <p className="state__body">
            Please try again in a moment. If it keeps happening, contact {contact}.
          </p>
        </div>
      )}

      {!failed && isBusy && (
        <div className="fd-loading" aria-busy="true" aria-live="polite">
          <span className="fd-loading__label">Loading what is outstanding…</span>
          <div className="skeleton" style={{ width: "35%" }} />
          <div className="skeleton" style={{ width: "80%" }} />
          <div className="skeleton" />
        </div>
      )}

      {!failed && !isBusy && isEmpty && (
        <EmptyState
          iconName="check-circle-2"
          title="Nothing outstanding"
          body="None of your onboarding files is waiting on anything right now."
        />
      )}

      {!failed && !isBusy && data && !isEmpty && (
        <>
          {/* The read's own instant, always visible — this page's half of "live" (Design §6). */}
          <LastUpdated timestamp={new Date(data.as_of)} />

          {/* Never a silent truncation: the words carry the EFFECTIVE limit off the envelope —
              what the engine clamped the request to, not what anyone asked for. */}
          {data.cap_hit && (
            <p className="oi-cap" role="status">
              There is more outstanding than fits on one page — showing the {data.limit} things you
              have been waiting on longest.
            </p>
          )}

          {groups.map((group) => {
            const name = fileDisplayName(group.clientName);
            return (
              <section className="oi-group" key={group.jobId}>
                <div className="oi-group__head">
                  {/* The id lives in this `href` and nowhere else on the page: a routing key, not
                      copy (Design §3). The link TEXT is the client's name, or the honest absence. */}
                  <Link
                    className={`oi-group__file${name.named ? "" : " oi-group__file--unnamed"}`}
                    to={`/onboarding/${group.jobId}`}
                  >
                    {name.text}
                  </Link>
                  {/* The stage, PLAINLY. This envelope carries `stage_label` without the
                      `stage_label_source` the board and the detail get, so this page cannot tell a
                      declared stage from a raw engine state id — and a badge would assert the
                      first. Rendering it quietly is the honest reading of what was sent. */}
                  <span className="oi-group__stage">{group.stageLabel}</span>
                  <span className="oi-group__count">
                    {group.items.length === 1 ? "1 thing owed" : `${group.items.length} things owed`}
                  </span>
                </div>
                <ul className="oi-list">
                  {group.items.map((row) => (
                    <OutstandingItemLine
                      key={outstandingItemKey(row.item)}
                      item={row.item}
                      now={now}
                    />
                  ))}
                </ul>
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}

export default OutstandingItemsPage;
